import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { useEffect, useRef, useState } from "react";
import { ColumnDef } from "../../models/orders/order-list-model";
import { useTranslation } from "react-i18next";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast, ToastMessage } from "primereact/toast";
import { TabPanel, TabView } from "primereact/tabview";
import { DataTableStateEvent } from "primereact/datatable";
import orderServices from "../../services/orders/orderservice";
import NewOrderReview from "../New-Invoice-Report/new-order-review";
import OrderPrint from "../New-Invoice-Report/new-order-print";
import NewCreateInvoice from "./new-create-invoice";
import { newmapInvoiceData } from "./new-invoice-mapper-model";
import InvoiceServices from "../../services/invoices/gen_inv.service";
import { selectedGetway } from "../../redux/slices/getway-link";
import RouteConstant from "../../constants/route.constants";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../redux/hooks";
import { GetWayLinkModel } from "../../models/getway-link/getway-link-model";

interface DataTableProps {
  data: any[];
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (event: DataTableStateEvent) => void;
  onRefresh?: () => void;
}

const GenerateInvoiceDataTable: React.FC<DataTableProps> = ({ 
  data, 
  pageNumber, 
  pageSize, 
  totalRecords, 
  onPageChange,
  onRefresh 
}) => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const [visible, setVisible] = useState(false);
  const [createInvoiceVisible, setCreateInvoiceVisible] = useState(false);
  const [dialogHeader, setDialogHeader] = useState(t("orderlist_orderdetails_label"));
  const [printEnabled, setPrintEnabled] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any>({});
  const [selectedOrder, setSelectedOrder] = useState<any>({});
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleSetOrderData = async (rowData: any) => {
    setVisible(true);
    try {
      const details = await orderServices.GetOrderByOrderId(rowData.orderID);
      if (details) {
        setOrderDetails(details);  
      } else {
        console.warn("No details found for order ID:", rowData.orderID);
        showMessage("Warning", "No details found for this order", "warn");
      }
    } catch (error) {
      console.error("Error while fetching order details:", error);
      showMessage("Error", "Failed to fetch order details", "error");
    }
  };

  const handleGenerateInvoice = async (rowData: any) => {
    try {
      const details = await orderServices.GetOrderByOrderIdForInvoice(rowData.orderID);
      if (details) {
        setSelectedOrder(details);
        setCreateInvoiceVisible(true);
      } else {
        console.warn("No details found for order ID:", rowData.orderID);
        showMessage("Warning", "No details found for this order", "warn");
      }
    } catch (error) {
      console.error("Error while fetching order details:", error);
      showMessage("Error", "Failed to fetch order details", "error");
    }
  };
 
  useEffect(() => {    
    if (orderDetails && Object.keys(orderDetails).length > 0) {
      setVisible(true);
    }
  }, [orderDetails]);
 
  const showMessage = (label: string, message: string, severity: ToastMessage['severity']) => {
    toast.current?.show({ severity, summary: label, detail: message, life: 3000 });
  };

  const mapAdditionalInfo = (data: any): GetWayLinkModel => ({
    paymentURL: data.paymentURL,
    expectedDeliveryDate: data.expectedDeliveryDate,
  });
  
  const handleCreateInvoice = async (event: any, payId: number) => {
    try {
      const checkoutPayId = event.amountPaid > 0 ? 0 : payId;
      const invoiceData = newmapInvoiceData(event, payId);
      const response = await InvoiceServices.GenerateNewInvoice(invoiceData);
      if (response.id > 0) {       
        showMessage("Success", `Invoice #${response.id} generated successfully`, 'success');  
        setCreateInvoiceVisible(false);     
        if (onRefresh) {
          await onRefresh();
        }    
        if (checkoutPayId === 7) {
          const getwayData = response.additionalInfo;
          dispatch(selectedGetway(mapAdditionalInfo(getwayData)));
          navigate(RouteConstant.getwayProcess);
        }
      } else {
        showMessage("Error", `${response.message}: Error in generate invoice`, 'error');
      }
    } catch (error) {
      showMessage("Error", 'Error in generate invoice', 'error');
    }
  };

  const columns: ColumnDef[] = [
    {
      field: "orderID",
      header: t("orderlist_table_orderno"),
      style: { minWidth: "7rem" },
      cell: (row: any) => (
        <span
          style={{ color: "darkcyan", fontWeight: "bolder", cursor: "pointer" }}
          onClick={() => handleSetOrderData(row)}
        >
          {row.orderID}
        </span>
      ),
    },
    {
      field: "createdDate",
      header: t("orderlist_table_orderdate"),
      style: { minWidth: "9rem" },
      cell: (row: any) =>
        new Date(row.createdDate).toLocaleDateString("en-gb", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      field: "userName",
      header: t("orderlist_table_username"),
      style: { minWidth: "10rem" },
      cell: (row: any) => row.userName,
    },
    {
      field: "primaryContactNumber",
      header: t("orderlist_table_mobile"),
      style: { minWidth: "8rem" },
      cell: (row: any) => row.primaryContactNumber,
    },
    {
      field: "action",
      header: t("Action"),
      style: { minWidth: "3rem", textAlign: "left" },
      cell: (row: any) => (
        <Button
          label="Generate Invoice"
          className="p-button p-button-success"
          onClick={() => handleGenerateInvoice(row)}
        />
      ),
    },
  ];

  const hideDialog = () => {
    setVisible(false);
    setDialogHeader(t("orderlist_orderdetails_label"));
  };

  const hideCreateInvoiceDialog = () => {
    setCreateInvoiceVisible(false);
  };
 
  const footerContent = (
    <div>
      <Button label="Close" icon="pi pi-times" onClick={hideDialog} className="p-button-text" />
      <Button label="Print" icon="pi pi-print" onClick={() => setPrintEnabled(true)} autoFocus />
    </div>
  );
 
  const handleTriggerPrint = (val: boolean) => {
    setPrintEnabled(val);
  };
 
  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div className="col-12 card border-noround p-2">
        {data && data.length > 0 ? (
          <DataTable
            value={data}
            paginator
            rows={pageSize}
            first={(pageNumber - 1) * pageSize}
            totalRecords={totalRecords}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            stripedRows
            showGridlines
            emptyMessage="No order found."
            sortMode="multiple"
            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
            onPage={onPageChange}
            lazy
          >
            {columns.map((col, i) => (
              <Column
                key={i}
                field={col.field}
                sortable={col.sortable}
                header={col.header}
                style={col.style}
                body={col.cell}
              />
            ))}
          </DataTable>
        ) : (
          <div className="p-4 text-center">No data available</div>
        )}
      </div>
      
      {orderDetails !== undefined || orderDetails !== null ? (
        <Dialog
        header={dialogHeader}
        visible={visible}
        style={{ width: "67vw" }}
        onHide={hideDialog}
        >
          <TabView>
            <TabPanel header="Order Details" leftIcon="pi pi-file mr-1">
              <NewOrderReview 
              TriggerPrint={handleTriggerPrint} 
              PrintEnabled={printEnabled} 
              OrderData={<OrderPrint orderinfo={orderDetails}/>} 
              />
            </TabPanel>
          </TabView>
        </Dialog>
      ) : null}

      <Dialog
        header="Generate Invoice"
        visible={createInvoiceVisible}
        style={{ width: "80vw", maxWidth: "1200px" }}
        onHide={hideCreateInvoiceDialog}
        className="create-invoice-dialog"
      >
        {selectedOrder && Object.keys(selectedOrder).length > 0 && (
          <NewCreateInvoice 
            OrderData={selectedOrder}
            runHideDialog={hideCreateInvoiceDialog}
            setOrderData={setSelectedOrder}
            runCreateInvoice={handleCreateInvoice}
          />
        )}
      </Dialog>
    </>
  );
};

export default GenerateInvoiceDataTable;